import { highlightError, setAttribute } from '../../form-generator/helpers/utility';
import { fillSizesInInventory, updateColumnsAccordingToSections } from '../../helpers/global';
import { makeApiRequests } from '../../helpers/api';
import { ENDPOINTS, additionalDocHubfieldTypesWithDropdowns } from '../../helpers/constants';
import { fieldAppliesToModel, getModelLabel } from '../../helpers/formHelpers';
import { buildMergedAdditionalFields, buildMergedCustomEntities } from '../../helpers/mergeAdditionalFields';
import { getCommissionConfigForInventory, fromCommissionTableRows } from './inventoryCommissionHelpers';

const headerRow = title => {
  return [
    {
      columns: [
        {
          default: 12,
          xs: 12,
          className: 'align-self-center',
          field: {
            type: 'custom-html',
            html: `<h3 class="mb-0"><b>${title}</b></h3><hr class="d-md-none"/>`
          }
        }
      ]
    },
    {
      columns: [
        {
          default: 12,
          field: {
            type: 'custom-html',
            html: '<hr class="my-1"/>'
          }
        }
      ]
    }
  ];
};

const validateSizeFormat = (size = '', formValues) => {
  if (!size) {
    return true;
  }

  const split = size
    ?.split('x')
    .filter(a => a.trim())
    .map(a => Number(a.trim()));

  if (split.length != 2 || split.includes(NaN)) {
    return 'Please enter size in the format of a x b';
  }

  return true;
};

const getSectionBasedColumns = (numberOfSections, key, label) => {
  return [
    {
      default: updateColumnsAccordingToSections(numberOfSections),
      xs: 12,
      field: {
        id: `${key}A`,
        title: `${label} #A`,
        type: 'text',
        required: false,
        validation: key === 'size' ? validateSizeFormat : undefined
      }
    },
    ...(numberOfSections > 1
      ? [
          {
            default: updateColumnsAccordingToSections(numberOfSections),
            xs: 12,
            field: {
              id: `${key}B`,
              title: `${label} #B`,
              type: 'text',
              required: false,
              validation: key === 'size' ? validateSizeFormat : undefined
            }
          }
        ]
      : []),
    ...(numberOfSections > 2
      ? [
          {
            default: updateColumnsAccordingToSections(numberOfSections),
            xs: 12,
            field: {
              id: `${key}C`,
              title: `${label} #C`,
              type: 'text',
              required: false,
              validation: key === 'size' ? validateSizeFormat : undefined
            }
          }
        ]
      : [])
  ];
};

const fieldRows = ({
  numberOfSections,
  onNumberOfSectionsChange,
  onSizeOverallChange,
  onManufacturerChange,
  onModelChange,
  onCreateModelClick,
  onUnitLocationChange,
  models = []
}) => [
  {
    columns: [
      {
        default: 3,
        xs: 12,
        field: {
          id: 'isStockInOrder',
          title: 'Stock On Order',
          key: 'isStockInOrder',
          type: 'dropdown',
          options: ['No', 'Yes'],
          required: true
        }
      },
      {
        default: 3,
        xs: 12,
        field: {
          id: 'isRSO',
          title: 'RSO',
          key: 'isRSO',
          type: 'dropdown',
          options: ['No', 'Yes'],
          required: true
        }
      },
      {
        default: 3,
        xs: 12,
        field: {
          id: 'unitLocation',
          title: 'Unit Location',
          type: 'dropdown',
          options: [],
          required: true,
          onChange: onUnitLocationChange
        }
      },
      {
        default: 3,
        xs: 12,
        field: {
          id: 'manufacturer',
          title: 'Manufacturer',
          type: 'searchable-dropdown',
          options: [],
          required: true,
          onChange: onManufacturerChange
        }
      }
    ]
  },
  {
    columns: [
      {
        default: 3,
        xs: 12,
        field: {
          id: 'model',
          title: 'Model',
          type: 'creatable-dropdown',
          options: models.map(getModelLabel),
          optionValues: models.map(m => m._id),
          required: true,
          onChange: onModelChange,
          onCreateOptionClick: onCreateModelClick
        }
      },

      {
        default: 3,
        xs: 12,
        field: {
          id: 'dateOfManufacture',
          title: 'Date of manufacture',
          type: 'date',
          required: false
        }
      },
      {
        default: 3,
        xs: 12,
        field: {
          id: 'vin',
          title: 'VIN (If Tiny Home)',
          type: 'text',
          required: false
        }
      },
      {
        default: 3,
        xs: 12,
        field: {
          id: 'exteriorSiding',
          title: 'Exterior Siding',
          type: 'dropdown',
          options: [],
          required: true
        }
      }
    ]
  },
  {
    columns: [
      {
        default: 3,
        xs: 12,
        field: {
          id: 'hasFurnitureInside',
          title: 'Has Furniture Inside',
          key: 'hasFurnitureInside',
          type: 'dropdown',
          options: ['No', 'Yes']
        }
      },
      {
        default: 3,
        xs: 12,
        field: {
          id: 'basePrice',
          key: 'basePrice',
          title: 'Base price',
          type: 'number'
        }
      },
      {
        default: 3,
        xs: 12,
        field: {
          id: 'options',
          key: 'options',
          title: 'Options',
          type: 'number'
        }
      },
      {
        default: 3,
        xs: 12,
        field: {
          id: 'freight',
          key: 'freight',
          title: 'Freight',
          type: 'number'
        }
      }
    ]
  },

  {
    columns: [
      {
        default: 4,
        xs: 12,
        field: {
          id: 'stateTax',
          title: 'State tax',
          key: 'stateTax',
          type: 'number'
        }
      },
      {
        default: 4,
        xs: 12,
        field: {
          id: 'numberOfSections',
          title: 'Number Of Sections',
          infoText: 'Changing this resets the Calculation Worksheet related fields.',
          type: 'dropdown',
          required: true,
          options: [1, 2, 3],
          onChange: onNumberOfSectionsChange
        }
      },
      {
        default: 4,
        xs: 12,
        field: {
          id: 'sizeOverall',
          title: 'Size Overall',
          type: 'text',
          required: false,
          onChange: onSizeOverallChange,
          validation: validateSizeFormat
        }
      }
    ]
  },
  {
    columns: [
      ...getSectionBasedColumns(numberOfSections, 'serial', 'Serial'),
      ...getSectionBasedColumns(numberOfSections, 'size', 'Size'),
      ...getSectionBasedColumns(numberOfSections, 'hud', 'HUD')
    ]
  },

  {
    columns: [
      {
        default: 4,
        xs: 12,
        field: {
          id: 'type',
          title: 'Type',
          type: 'dropdown',
          options: [],
          required: true
        }
      },
      {
        default: 4,
        xs: 12,
        field: {
          id: 'year',
          title: 'Year',
          type: 'text',
          required: false
        }
      },
      {
        default: 4,
        xs: 12,
        field: {
          id: 'windZone',
          title: 'Wind Zone',
          type: 'text',
          required: false
        }
      }
    ]
  },

  {
    columns: [
      {
        default: 4,
        xs: 12,
        field: {
          id: 'invoiceDate',
          title: 'Invoice Date',
          type: 'date',
          required: false
        }
      },
      {
        default: 4,
        xs: 12,
        field: {
          id: 'invoice',
          title: 'Invoice',
          type: 'number',
          required: false
        }
      },
      {
        default: 4,
        xs: 12,
        field: {
          id: 'vepCode',
          title: 'VEP Code',
          type: 'dropdown',
          required: false,
          options: ['0', '1', '2']
        }
      }
    ]
  },
  {
    columns: [
      {
        default: 3,
        xs: 12,
        field: {
          id: 'floorplanFinanceAmount',
          title: 'Floorplan Finance Amount',
          type: 'number',
          required: false
        }
      },
      {
        default: 3,
        xs: 12,
        field: {
          id: 'bedRooms',
          title: 'Bedrooms',
          type: 'text',
          required: false
        }
      },
      {
        default: 3,
        xs: 12,
        field: {
          id: 'bathRooms',
          title: 'Bathrooms',
          type: 'text',
          required: false
        }
      },
      {
        default: 3,
        xs: 12,
        field: {
          options: [],
          required: false,
          title: 'Floorplan Lender',
          type: 'dropdown',
          id: 'floorplanLender'
        }
      }
    ]
  },
  {
    columns: [
      {
        default: 4,
        xs: 12,
        field: {
          options: ['No', 'Yes'],
          required: true,
          title: 'Lot Set',
          type: 'dropdown',
          id: 'lotSet'
        }
      },
      {
        default: 4,
        xs: 12,
        field: {
          id: 'typeOfUnit',
          title: 'Type of Unit',
          type: 'dropdown',
          required: true
        }
      },
      {
        default: 4,
        xs: 12,
        field: {
          id: 'weightA',
          title: 'Weight A',
          type: 'text',
          required: false
        }
      }
    ]
  },
  {
    columns: [
      {
        default: 4,
        xs: 12,
        field: {
          id: 'weightB',
          title: 'Weight B',
          type: 'text',
          required: false
        }
      },
      {
        default: 4,
        xs: 12,
        field: {
          id: 'sqFt',
          title: 'Sq Ft',
          type: 'number',
          required: false
        }
      },
      {
        default: 4,
        xs: 12,
        field: {
          id: 'thermalZone',
          title: 'Thermal Zone',
          type: 'text',
          required: false
        }
      }
    ]
  },
  {
    columns: [
      {
        default: 4,
        xs: 12,
        field: {
          id: 'roofLoad',
          title: 'Roof Load',
          type: 'text',
          required: false,
          value: '20 PSF'
        }
      },
      {
        default: 4,
        xs: 12,
        field: {
          id: 'floorRValues',
          title: 'Floor R Values',
          type: 'text',
          required: false
        }
      },
      {
        default: 4,
        xs: 12,
        field: {
          id: 'wallRValues',
          title: 'Walls R Values',
          type: 'text',
          required: false
        }
      }
    ]
  },
  {
    columns: [
      {
        default: 4,
        xs: 12,
        field: {
          id: 'ceilingRValues',
          title: 'Ceiling R Values',
          type: 'text',
          required: false
        }
      },
      {
        default: 4,
        xs: 12,
        field: {
          id: 'floorThickness',
          title: 'Floor Thickness',
          type: 'number',
          required: false
        }
      },
      {
        default: 4,
        xs: 12,
        field: {
          id: 'wallThickness',
          title: 'Walls Thickness',
          type: 'number',
          required: false
        }
      }
    ]
  },
  {
    columns: [
      {
        default: 4,
        xs: 12,
        field: {
          id: 'ceilingThickness',
          title: 'Ceiling Thickness',
          type: 'number',
          required: false
        }
      },
      {
        default: 4,
        xs: 12,
        field: {
          id: 'height',
          title: 'Height',
          type: 'number',
          required: false
        }
      },
      {
        default: 4,
        xs: 12,
        field: {
          id: 'maxSalesPrice',
          title: 'Max Sales Price',
          type: 'number',
          required: false
        }
      }
    ]
  },
  {
    columns: [
      {
        default: 4,
        xs: 12,
        field: {
          id: 'furnaceManufacturer',
          title: 'Furnace Manufacturer',
          type: 'text',
          required: false
        }
      },
      {
        default: 4,
        xs: 12,
        field: {
          id: 'furnaceModelNumber',
          title: 'Furnace Model Number',
          type: 'text',
          required: false
        }
      },
      {
        default: 4,
        xs: 12,
        field: {
          id: 'btuPerHour',
          title: 'B.T.U/hr',
          type: 'number',
          required: false
        }
      }
    ]
  },
  {
    rowName: 'Commission Options',
    collapsible: true,
    defaultCollapsed: true,
    columns: [
      {
        default: 6,
        xs: 12,
        field: {
          id: 'commissionSheetOptions',
          key: 'commissionSheetOptions',
          title: 'Commission Sheet Options',
          type: 'table-input',
          tableFields: [
            { title: 'Commission Sheet Option', key: 'key', type: 'datalist-text', required: false },
            { title: 'Value', key: 'value', type: 'number', required: false }
          ],
          required: false,
          includeUnselectedKeys: true,
          showResetToDefault: true
        }
      },
      {
        default: 6,
        xs: 12,
        field: {
          id: 'commissionSheetLessOptions',
          key: 'commissionSheetLessOptions',
          title: 'Commission Sheet Less Options',
          type: 'table-input',
          tableFields: [
            { title: 'Commission Sheet Less Option', key: 'key', type: 'datalist-text', required: false },
            { title: 'Value', key: 'value', type: 'number', required: false }
          ],
          required: false,
          includeUnselectedKeys: true,
          showResetToDefault: true
        }
      }
    ]
  }
];

export const formJson = ({
  numberOfSections,
  onNumberOfSectionsChange,
  onSizeOverallChange,
  onManufacturerChange,
  onModelChange,
  onCreateModelClick,
  onUnitLocationChange,
  models = []
}) => ({
  forms: [
    {
      name: 'addInventory',
      hideFormName: true,
      markCompulsoryFields: true,
      compact: true,
      submit: {
        name: 'Add To Inventory',
        show: false,
        onSubmit: 'onInventoryFormSubmit'
      },
      rows: fieldRows({
        numberOfSections,
        onNumberOfSectionsChange,
        onSizeOverallChange,
        onManufacturerChange,
        onModelChange,
        onCreateModelClick,
        onUnitLocationChange,
        models
      })
    }
  ]
});

export const editFormJson = ({
  fromPipeline = false,
  numberOfSections,
  onNumberOfSectionsChange,
  onSizeOverallChange,
  onManufacturerChange,
  onModelChange,
  onCreateModelClick,
  onUnitLocationChange,
  models = []
}) => {
  return {
    forms: [
      {
        name: 'update',
        hideFormName: true,
        markCompulsoryFields: true,
        compact: true,
        submit: {
          name: 'Update Record',
          show: fromPipeline,
          onSubmit: 'onInventoryEditFormSubmit'
        },
        rows: fieldRows({
          numberOfSections,
          onNumberOfSectionsChange,
          onSizeOverallChange,
          onManufacturerChange,
          onModelChange,
          onCreateModelClick,
          onUnitLocationChange,
          models
        })
      }
    ]
  };
};

//used during setup
export const addChoicesToInventoryForm = ({
  appChoices,
  form,
  modelOptions,
  modelOptionValues,
  unitLocationId,
  numberOfSections,
  commissionResetHandlers = {}
}) => {
  const requiredChoices = [
    { appChoicesKey: 'salesLocation', formKey: 'unitLocation', labelKey: 'location' },
    { appChoicesKey: 'lender', formKey: 'lender', labelKey: 'lienholderCorporateName' },
    { appChoicesKey: 'floorplanLender', formKey: 'floorplanLender' },
    { appChoicesKey: 'Type', formKey: 'type' },
    { appChoicesKey: 'Exterior Siding', formKey: 'exteriorSiding' },
    { appChoicesKey: 'manufacturer', formKey: 'manufacturer', labelKey: 'factoryShortName' },
    { appChoicesKey: 'Type Of Unit', formKey: 'typeOfUnit' }
  ];

  const additionalDocHubFieldsToTakeAppChoices = (appChoices.find(a => a.key === 'formFields')?.values || []).filter(
    v =>
      additionalDocHubfieldTypesWithDropdowns.includes(v.type) &&
      fieldAppliesToModel(v, 'Inventory') &&
      v.takeOptionsFrom === 'appChoice'
  );

  requiredChoices.push(
    ...additionalDocHubFieldsToTakeAppChoices.map(f => ({
      appChoicesKey: f.optionAppChoiceName,
      formKey: `${f.key}-additionalDochubField`
    }))
  );

  requiredChoices.forEach(requiredChoice => {
    const choice = appChoices.find(a => a.key === requiredChoice.appChoicesKey);

    if (choice && choice.values.length > 0 && typeof choice.values[0] === 'object') {
      setAttribute(
        form,
        requiredChoice.formKey,
        'options',
        choice.values.map(c => c[requiredChoice.labelKey || 'name'])
      );
      setAttribute(
        form,
        requiredChoice.formKey,
        'optionValues',
        choice.values.map(c => c._id)
      );
      return;
    }

    setAttribute(form, requiredChoice.formKey, 'options', choice ? choice.values : []);
  });

  // Set model options based on manufacturer
  if (modelOptions && modelOptionValues) {
    setAttribute(form, 'model', 'options', modelOptions);
    setAttribute(form, 'model', 'optionValues', modelOptionValues);
  }

  const { commissionOptions, commissionLessOptions } = getCommissionConfigForInventory(
    appChoices,
    unitLocationId,
    numberOfSections
  );

  const toDropdownOptions = defs => defs.map(({ name }) => [name]);

  setAttribute(form, 'commissionSheetOptions', 'dropdownOptions', toDropdownOptions(commissionOptions));
  setAttribute(form, 'commissionSheetLessOptions', 'dropdownOptions', toDropdownOptions(commissionLessOptions));

  if (commissionResetHandlers.commissionSheetOptions) {
    setAttribute(form, 'commissionSheetOptions', 'onResetToDefault', commissionResetHandlers.commissionSheetOptions);
  }
  if (commissionResetHandlers.commissionSheetLessOptions) {
    setAttribute(
      form,
      'commissionSheetLessOptions',
      'onResetToDefault',
      commissionResetHandlers.commissionSheetLessOptions
    );
  }
};

//used after submit
export const validateAndCleanupForm = (submittedForm, existingAdditionalFields, existingCustomEntities) => {
  //for additional dochub fields created by user (merge so keys not on this form are kept)
  submittedForm['additionalFields'] = buildMergedAdditionalFields(submittedForm, existingAdditionalFields);
  submittedForm['customEntities'] = buildMergedCustomEntities(submittedForm, existingCustomEntities);

  if (submittedForm.commissionSheetOptions) {
    submittedForm.commissionSheetOptions = fromCommissionTableRows(submittedForm.commissionSheetOptions);
  }

  if (submittedForm.commissionSheetLessOptions) {
    submittedForm.commissionSheetLessOptions = fromCommissionTableRows(submittedForm.commissionSheetLessOptions);
  }

  return true;
};
